import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import { DataTable, type Column } from "../components/ui/data-table";
import { DateRangePicker } from "../components/ui/date-range-picker";
import { useToast } from "../components/ui/toaster";
import { InvoiceDetailsModal } from "../components/invoice-details-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faRefresh,
  faUsers,
  faFileInvoice,
  faDollarSign,
  faPercentage,
  faExclamationTriangle,
  faBan,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import type { SalespersonCommission } from "@/shared/types";
import { useCommissionData } from "../hooks/useCommissionData";
import {
  formatCurrency,
  getCommissionPeriod,
  getCustomDateRange,
  toBangkokISOString,
} from "../lib/utils";
import { exportCommissionExcel } from "../lib/export-excel";
import { useEffectiveIsAdmin } from "../contexts/ViewAsRoleContext";
import { t } from "@/shared/lib/th-translations";
import { RoleSelector } from "../components/RoleSelector";

// Define DateRange type since it's not exported from react-day-picker v9
type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();

  // Check if effective role is admin (uses preview role if active)
  const isAdmin = useEffectiveIsAdmin();

  // Parse URL parameters for date range
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");
  const yearParam = searchParams.get("year");
  const monthParam = searchParams.get("month");

  // Determine initial period
  let initialPeriod;
  if (startDateParam && endDateParam) {
    const startDate = new Date(startDateParam);
    const endDate = new Date(endDateParam);
    initialPeriod = getCustomDateRange(startDate, endDate);
  } else if (yearParam && monthParam) {
    const year = parseInt(yearParam);
    const month = parseInt(monthParam);
    const periodDate = new Date(year, month - 1, 26);
    initialPeriod = getCommissionPeriod(periodDate);
  } else {
    initialPeriod = getCommissionPeriod();
  }

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => ({
    from: initialPeriod.startDate,
    to: initialPeriod.endDate,
  }));

  const [isExporting, setIsExporting] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedSalesperson, setSelectedSalesperson] = useState<{
    id: string;
    name: string;
    employeeNumber?: string | null;
  } | null>(null);

  // Convert date range to period for API call
  const commissionPeriod =
    dateRange?.from && dateRange?.to
      ? {
        year: dateRange.to.getFullYear(),
        month: dateRange.to.getMonth() + 1,
      }
      : undefined;

  // Fetch commission data
  const { salespersons, period, stats, excludedSummary, creditNoteSummary, loading, error, refetch } =
    useCommissionData({
      period: commissionPeriod,
      includeStats: true,
      useAggregations: false,
      isAdmin,
    });

  // Transform salesperson data to display employee number when available
  const displaySalespersons = salespersons.map(sp => ({
    ...sp,
    salespersonName: sp.employeeNumber || sp.salespersonName
  }));

  // Table columns configuration
  const allColumns: Column<SalespersonCommission>[] = [
    {
      key: "salespersonName",
      label: t("dashboard.table.employee"),
      sortable: true,
      align: "left",
    },
    {
      key: "invoiceCount",
      label: t("dashboard.table.invoiceCount"),
      sortable: true,
      align: "center",
      format: "number",
      clickable: true,
      formatFunction: (value: number, row?: SalespersonCommission) => {
        if (!row) return value.toString();
        
        // Only show x/y pattern for admin access AND when there are filtered invoices
        if (isAdmin && row.totalInvoiceCount && row.totalInvoiceCount > row.invoiceCount) {
          return `${row.invoiceCount}/${row.totalInvoiceCount}`;
        }
        
        // For non-admin or when no filtering, just show the count
        return row.invoiceCount.toString();
      },
    },
    {
      key: "totalSales",
      label: t("dashboard.table.totalSales"),
      sortable: true,
      align: "right",
      format: "currency",
      ...(isAdmin ? {
        renderFunction: (value: number, row?: SalespersonCommission) => {
          if (!row || !row.totalSalesBeforeExclusion || row.totalSalesBeforeExclusion === row.totalSales) {
            return formatCurrency(value);
          }
          return (
            <div className="text-right">
              <div className="text-orange-500 font-semibold">{formatCurrency(row.totalSales)}</div>
              <div className="text-green-700 text-xs">{formatCurrency(row.totalSalesBeforeExclusion)}</div>
            </div>
          );
        },
      } : {}),
    },
    {
      key: "paidAmountInPeriod",
      label: t("dashboard.table.paidThisPeriod"),
      sortable: true,
      align: "right",
      format: "currency",
      mobileHidden: true,
      ...(isAdmin ? {
        renderFunction: (value: number, row?: SalespersonCommission) => {
          if (!row || !row.paidAmountBeforeExclusion || row.paidAmountBeforeExclusion === row.paidAmountInPeriod) {
            return formatCurrency(value);
          }
          return (
            <div className="text-right">
              <div className="text-orange-500 font-semibold">{formatCurrency(row.paidAmountInPeriod)}</div>
              <div className="text-green-700 text-xs">{formatCurrency(row.paidAmountBeforeExclusion)}</div>
            </div>
          );
        },
      } : {}),
    },
    {
      key: "commissionableAmount",
      label: t("dashboard.table.commissionable"),
      sortable: true,
      align: "right",
      format: "currency",
      mobileHidden: true,
      formatFunction: (value: number, row?: SalespersonCommission) => {
        if (!row || !row.commissionableAmount) return "-";
        // Commented out for now
        // const percentage = row.paidAmountInPeriod > 0
        //   ? ((row.commissionableAmount / row.paidAmountInPeriod) * 100).toFixed(1)
        //   : "0";
        // return `${formatCurrency(row.commissionableAmount)} (${percentage}%)`;
        return `${formatCurrency(row.commissionableAmount)}`;
      },
    },
    {
      key: "nonCommissionableAmount",
      label: t("dashboard.table.nonComm"),
      sortable: true,
      align: "right",
      format: "currency",
      mobileHidden: true,
      className: "text-gray-500",
      formatFunction: (value: number, row?: SalespersonCommission) => {
        if (!row || !row.nonCommissionableAmount) return "-";
        // Commented out for now
        // const percentage = row.paidAmountInPeriod > 0
        //   ? ((row.nonCommissionableAmount / row.paidAmountInPeriod) * 100).toFixed(1)
        //   : "0";
        // return `${formatCurrency(row.nonCommissionableAmount)} (${percentage}%)`;
        return `${formatCurrency(row.nonCommissionableAmount)}`;
      },
    },
    {
      key: "pendingCollection",
      label: t("dashboard.table.pendingCollection"),
      sortable: true,
      align: "right",
      format: "currency",
      mobileHidden: true,
      ...(isAdmin ? {
        renderFunction: (value: number, row?: SalespersonCommission) => {
          if (!row || !row.pendingCollectionBeforeExclusion || row.pendingCollectionBeforeExclusion === row.pendingCollection) {
            return formatCurrency(value ?? 0);
          }
          return (
            <div className="text-right">
              <div className="text-orange-500 font-semibold">{formatCurrency(row.pendingCollection ?? 0)}</div>
              <div className="text-green-700 text-xs">{formatCurrency(row.pendingCollectionBeforeExclusion)}</div>
            </div>
          );
        },
      } : {}),
    },
    {
      key: "creditNotesVatInclusive",
      label: t("dashboard.table.creditNotes"),
      sortable: true,
      align: "right",
      format: "currency",
      mobileHidden: false,
      className: "text-red-600",
      formatFunction: (value: number) => value > 0 ? `-${formatCurrency(value)}` : "-",
    },
    {
      key: "totalCommissionAmount",
      label: t("dashboard.table.totalCommission"),
      sortable: true,
      align: "right",
      format: "currency",
      highlight: true,
    },
  ];

  // Filter columns based on admin status
  const columns = isAdmin
    ? allColumns
    : allColumns.filter(col =>
        col.key !== "nonCommissionableAmount" &&
        col.key !== "paidAmountInPeriod" &&
        col.key !== "pendingCollection"
      );

  // Handle date range change
  const handleDateRangeChange = (newDateRange: DateRange | undefined) => {
    setDateRange(newDateRange);

    if (newDateRange?.from && newDateRange?.to) {
      // Navigate with date range parameters in Bangkok timezone
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("startDate", toBangkokISOString(newDateRange.from));
      newSearchParams.set("endDate", toBangkokISOString(newDateRange.to));
      // Remove year/month params if they exist (since we're using date range)
      newSearchParams.delete("year");
      newSearchParams.delete("month");
      navigate(`/?${newSearchParams}`);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Handle cell click (for invoice details modal)
  const handleCellClick = (
    row: SalespersonCommission,
    column: Column<SalespersonCommission>,
  ) => {
    if (column.key === "invoiceCount" && row.invoiceCount > 0) {
      setSelectedSalesperson({
        id: row.salespersonId,
        name: row.salespersonName,
        employeeNumber: row.employeeNumber,
      });
      setIsInvoiceModalOpen(true);
    }
  };

  // Handle export using frontend data
  const handleExport = async (format: "excel" | "json") => {
    setIsExporting(true);
    try {
      if (!dateRange?.from || !dateRange?.to) {
        throw new Error(t("dashboard.messages.selectDateRange"));
      }

      if (!salespersons || salespersons.length === 0) {
        throw new Error("No data to export");
      }

      if (format === "excel") {
        await exportCommissionExcel({
          salespersons,
          stats: stats ?? undefined,
          period: period ?? undefined,
        });
      } else {
        const jsonData = {
          period: {
            year: period?.year,
            month: period?.month,
            startDate: period?.startDate,
            endDate: period?.endDate,
          },
          stats,
          salespersons,
          exportedAt: new Date().toISOString(),
        };
        const content = JSON.stringify(jsonData, null, 2);
        const mimeType = "application/json;charset=utf-8;";
        const filename = `commission-report-${period?.year || "current"}-${(period?.month || 1).toString().padStart(2, "0")}.json`;

        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      addToast({
        title: t("dashboard.messages.exportSuccess"),
        description: t("dashboard.messages.exportSuccessMessage", { format: format.toUpperCase() }),
        duration: 3000,
      });
    } catch (err) {
      addToast({
        title: t("dashboard.messages.exportFailed"),
        description: err instanceof Error ? err.message : t("dashboard.messages.exportFailedMessage"),
        duration: 5000,
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto py-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 font-bold">{t("status.error")}</p>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      <div className="space-y-8 px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="space-y-1 min-w-0 flex-shrink">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl sm:text-3xl font-bold">{t("dashboard.title")}</h1>
            </div>
            <p className="text-muted-foreground text-sm sm:text-base">
              Track and analyze salesperson commissions by period
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              placeholder="Select date range"
            />
            <Button
              onClick={handleRefresh}
              disabled={loading}
              size="sm"
              variant="outline"
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            {/* Export buttons - Admin only */}
            {isAdmin && (
              <>
                <Button
                  onClick={() => handleExport("excel")}
                  disabled={isExporting}
                  size="sm"
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-2 h-4 w-4" />
                  {t("actions.exportExcel")}
                </Button>
                <Button
                  onClick={() => handleExport("json")}
                  disabled={isExporting}
                  variant="outline"
                  size="sm"
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-2 h-4 w-4" />
                  {t("actions.exportJSON")}
                </Button>
              </>
            )}
            <RoleSelector />
          </div>
        </div>

        {/* Admin Exclusion Summary and Credit Notes Row */}
        {((isAdmin && excludedSummary && excludedSummary.totalExcluded > 0) ||
          (creditNoteSummary && creditNoteSummary.totalCount > 0)) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Invoice Exclusions Card */}
            {isAdmin && excludedSummary && excludedSummary.totalExcluded > 0 && (
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
                      <FontAwesomeIcon
                        icon={faBan}
                        className="h-5 w-5 text-orange-600"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">
                        {t("dashboard.status.invoiceExclusionsActive")}
                      </p>
                      <p className="text-xs text-gray-600">
                        {excludedSummary.totalExcluded} invoice(s) excluded • {formatCurrency(excludedSummary.totalAmount)} total amount
                      </p>
                    </div>
                    <span className="text-xs text-orange-600 font-medium px-2 py-1 bg-orange-100 rounded">
                      {t("dashboard.status.adminMode")}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Credit Notes Card */}
            {creditNoteSummary && creditNoteSummary.totalCount > 0 && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                      <FontAwesomeIcon
                        icon={faReceipt}
                        className="h-5 w-5 text-yellow-600"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">
                        {t("dashboard.status.creditNotesApplied")}
                      </p>
                      <p className="text-xs text-gray-600">
                        {creditNoteSummary.totalCount} credit note(s) • {formatCurrency(creditNoteSummary.totalAmount)} total deduction
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{t("dashboard.status.preTaxDeduction")}</p>
                      <p className="text-sm font-semibold text-yellow-700">
                        -{formatCurrency(creditNoteSummary.totalPreTaxAmount)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-4">
                <Card className="bg-blue-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {t("dashboard.stats.salespersons")}
                        </p>
                        <p className="text-lg font-bold text-gray-900">
                          {stats.totalSalespersons}
                        </p>
                      </div>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                        <FontAwesomeIcon
                          icon={faUsers}
                          className="h-4 w-4 text-blue-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {t("dashboard.stats.invoices")}
                        </p>
                        <p className="text-lg font-bold text-gray-900">
                          {isAdmin && stats.totalInvoiceCountIncludingExcluded && stats.totalInvoiceCountIncludingExcluded !== stats.totalInvoices
                            ? (
                              <>
                                <span className="text-orange-500 font-bold">{stats.totalInvoices}</span>
                                <span className="text-green-700">/</span>
                                <span className="text-green-700">{stats.totalInvoiceCountIncludingExcluded}</span>
                              </>
                            )
                            : stats.totalInvoices}
                        </p>
                      </div>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                        <FontAwesomeIcon
                          icon={faFileInvoice}
                          className="h-4 w-4 text-green-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {t("dashboard.stats.periodSales")}
                        </p>
                        {isAdmin && stats.totalSalesIncludingExcluded && stats.totalSalesIncludingExcluded !== stats.totalSales
                          ? (
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-orange-500 truncate">{formatCurrency(stats.totalSales)}</p>
                              <p className="text-xs text-green-700 truncate">{formatCurrency(stats.totalSalesIncludingExcluded)}</p>
                            </div>
                          )
                          : <p className="text-lg font-bold text-gray-900">{formatCurrency(stats.totalSales)}</p>
                        }
                      </div>
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100">
                        <FontAwesomeIcon
                          icon={faDollarSign}
                          className="h-4 w-4 text-purple-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-cyan-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {t("dashboard.stats.paidInPeriod")}
                        </p>
                        {isAdmin && stats.totalPaidIncludingExcluded && stats.totalPaidIncludingExcluded !== stats.totalPaidInPeriod
                          ? (
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-orange-500 truncate">{formatCurrency(stats.totalPaidInPeriod)}</p>
                              <p className="text-xs text-green-700 truncate">{formatCurrency(stats.totalPaidIncludingExcluded)}</p>
                            </div>
                          )
                          : <p className="text-lg font-bold text-gray-900">{formatCurrency(stats.totalPaidInPeriod)}</p>
                        }
                      </div>
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-100">
                        <FontAwesomeIcon
                          icon={faDollarSign}
                          className="h-4 w-4 text-cyan-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {isAdmin && (
                <Card className="bg-red-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {t("dashboard.stats.pendingCollection")}
                        </p>
                        <p className="text-lg font-bold text-gray-900">
                          {stats.totalPendingCollectionIncludingExcluded && stats.totalPendingCollectionIncludingExcluded !== stats.totalPendingCollection
                            ? (
                              <>
                                <span className="text-orange-500 font-bold">{formatCurrency(stats.totalPendingCollection || 0)}</span>
                                <span className="text-green-700">/</span>
                                <span className="text-green-700 text-sm">{formatCurrency(stats.totalPendingCollectionIncludingExcluded)}</span>
                              </>
                            )
                            : formatCurrency(stats.totalPendingCollection || 0)}
                        </p>
                      </div>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="h-4 w-4 text-red-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                )}
                <Card className="bg-orange-50">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          {t("dashboard.stats.commission")}
                        </p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(stats.totalCommissionAmount)}
                        </p>
                      </div>
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100">
                        <FontAwesomeIcon
                          icon={faPercentage}
                          className="h-4 w-4 text-orange-600"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
          </div>
        )}

        {/* Commission Table */}
        <div className="w-full min-w-0 rounded-xl border border-gray-200 bg-white shadow">
          
          {/* Table Content - no extra wrappers */}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <DataTable
              data={displaySalespersons}
              columns={columns}
              className="w-full"
              onCellClick={handleCellClick}
            />
          )}
        </div>
      </div>

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setSelectedSalesperson(null);
        }}
        salespersonId={selectedSalesperson?.id || null}
        salespersonName={selectedSalesperson?.name || null}
        employeeNumber={selectedSalesperson?.employeeNumber}
        startDate={dateRange?.from?.toISOString() || null}
        endDate={dateRange?.to?.toISOString() || null}
        isAdmin={isAdmin}
        onCommissionRecalculated={refetch}
      />
    </div>
  );
}
